import React from 'react'
import ReactGA from 'react-ga4'
import MainLoader from '../components/layout/main-loader.component'
import { Toast } from '../components/layout/toast.component'
import { Provider } from 'react-redux'
import { Router, Redirect, Switch } from 'react-router-dom'

import { AppRoute, ScrollToTop, PrivateRoute } from '../components/router'

import { routes } from '../constants/routes.constants'
import { RoleConstants } from '../constants'
import { history } from '../helpers/history'
import module from '../helpers/module'
import store from '../helpers/store'
import { Creators as BonusPointsCreators } from '../ducks/bonus-points/bonus-points'
import { Creators as SiteCreators } from '../ducks/site/site'

import {
	SiteLayout,
	ClientLayout,
	PartnerLayout,
	EmployeeLayout,
} from '../layouts'

import { AxiosService } from '../services/axios.service'
import { LoginModule, RegisterModule } from './authentication.routes'

import * as SiteRoutes from './site.routes'
import * as ClientRoutes from './client.routes'
import * as PartnerRoutes from './partner.routes'
import * as EmployeeRoutes from './employee.routes'
import * as IdentityRoutes from './identity.routes'
import ErrorBoundary from '../components/error-boundary/ErrorBoundary'
import { SystemConfigService } from '../services/system-config.service'

AxiosService.init()

/**
 * IIFE to register a module with store, component and reducer
 */

const Register = module(store)

history.listen((location) => {
	ReactGA.send({ hitType: 'pageview', page: location.pathname })
})

const RootRoutes = () => {
	store.dispatch((dispatch) =>
		SystemConfigService.getRouteDoesNotAllowDiscountAsync().then(
			({ data }) => {
				dispatch(SiteCreators.invalidDiscountRoutes(data))
			}
		)
	)

	return (
		<Switch>
			<AppRoute
				exact
				path="/"
				layout={SiteLayout}
				module={SiteRoutes.LaunchModule}
				noPadding
				adaptHeaderBG
			/>

			<AppRoute
				path="/muviflex-iframe"
				module={SiteRoutes.IFramePlanaltoModule}
			/>

			<AppRoute
				path="/intermunicipal/seguro"
				module={SiteRoutes.IntermunicipalInsuranceModule}
			/>

			<AppRoute
				path={routes.SIMULADOR}
				layout={SiteLayout}
				module={SiteRoutes.PointsExchangeModule}
				adaptHeaderBG
			/>

			<AppRoute
				path={routes.FAQ}
				layout={SiteLayout}
				module={SiteRoutes.FaqModule}
				adaptHeaderBG
			/>

			<AppRoute
				path={routes.REGULAMENTO}
				layout={SiteLayout}
				module={SiteRoutes.RegulationModule}
				adaptHeaderBG
			/>

			<AppRoute
				path={routes.REBOOKING}
				layout={SiteLayout}
				module={SiteRoutes.RebookingModule}
				adaptHeaderBG
			/>

			<AppRoute
				path={routes.BENEFITS}
				layout={SiteLayout}
				module={SiteRoutes.BenefitsModule}
				adaptHeaderBG
			/>

			<AppRoute
				path={routes.ABOUT}
				layout={SiteLayout}
				module={SiteRoutes.AboutModule}
				adaptHeaderBG
			/>

			<AppRoute
				path={routes.INFO}
				layout={SiteLayout}
				module={SiteRoutes.InfoModule}
				adaptHeaderBG
			/>

			<AppRoute
				path={routes.ACCESSIBILITY}
				layout={SiteLayout}
				module={SiteRoutes.AccessibilityModule}
				adaptHeaderBG
			/>

			<AppRoute
				exact
				path={routes.MUVICARD}
				layout={SiteLayout}
				module={SiteRoutes.MuvicardModule}
				adaptHeaderBG
			/>

			<AppRoute
				exact
				path={routes.MUVIBUS}
				layout={SiteLayout}
				module={SiteRoutes.MuvibusModule}
				adaptHeaderBG
			/>

			<AppRoute
				exact
				path={routes.MUVICARD_CADASTRO}
				layout={SiteLayout}
				module={SiteRoutes.MuvicardRegisterModule}
				adaptHeaderBG
			/>

			<AppRoute
				exact
				path={routes.LOGIN_SOCIAL_CADASTRO}
				layout={SiteLayout}
				module={SiteRoutes.SocialLoginRegisterModule}
				adaptHeaderBG
			/>

			<AppRoute
				exact
				path={routes.MEU_CARTAO}
				layout={SiteLayout}
				module={SiteRoutes.MeuCartaoModule}
				adaptHeaderBG
			/>

			<AppRoute
				exact
				path={routes.MEU_CARTAO_REDEFINIR_SENHA}
				layout={SiteLayout}
				module={SiteRoutes.MuvicardResetPasswordModule}
				adaptHeaderBG
			/>

			<AppRoute
				exact
				path={routes.MEU_CARTAO_CANCELAR}
				layout={SiteLayout}
				module={SiteRoutes.MuvicardCancelModule}
				adaptHeaderBG
			/>

			<AppRoute
				exact
				path={routes.MUVICARD_REGULAMENTO}
				layout={SiteLayout}
				module={SiteRoutes.MuvicardRegulationModule}
				adaptHeaderBG
			/>

			<AppRoute
				path={routes.POLITICAS}
				layout={SiteLayout}
				module={SiteRoutes.PolicyModule}
				adaptHeaderBG
			/>

			<AppRoute
				path="/confirmacao-email/:id"
				layout={SiteLayout}
				module={IdentityRoutes.EmailConfirmationModule}
				adaptHeaderBG
			/>

			<AppRoute
				path="/redefinir-senha/:id"
				layout={SiteLayout}
				module={IdentityRoutes.RedefinePasswordModule}
				adaptHeaderBG
			/>

			<AppRoute
				path={routes.ENTRAR}
				layout={SiteLayout}
				module={LoginModule}
				adaptHeaderBG
			/>

			<AppRoute
				path={routes.CADASTRO}
				layout={SiteLayout}
				module={RegisterModule}
				adaptHeaderBG
			/>

			<AppRoute
				path={'/loja/produto/:id'}
				layout={SiteLayout}
				module={SiteRoutes.StoreProductDetailModule}
				adaptHeaderBG
			/>

			<AppRoute
				exact
				path={routes.COMPRA_DE_PASSAGEM}
				layout={SiteLayout}
				module={SiteRoutes.TicketSaleModule}
				adaptHeaderBG
			/>

			<AppRoute
				path={routes.CONSULTA_DE_PASSAGEM + '/:orderid'}
				layout={SiteLayout}
				module={SiteRoutes.TicketModule}
				adaptHeaderBG
			/>

			<PrivateRoute
				exact
				path={routes.ADMIN}
				layout={EmployeeLayout}
				module={EmployeeRoutes.EmployeeModule}
				roles={[
					RoleConstants.Admin,
					RoleConstants.Sac,
					RoleConstants.SacUp,
					RoleConstants.Financial,
				]}
			/>

			<PrivateRoute
				path={`${routes.ADMIN}/cliente/:id`}
				layout={EmployeeLayout}
				module={EmployeeRoutes.AdminUpdateClientInfoModule}
				roles={[RoleConstants.Admin]}
			/>

			<PrivateRoute
				path={`${routes.ADMIN}/clientes`}
				layout={EmployeeLayout}
				module={EmployeeRoutes.AdminClientSearchModule}
				roles={[RoleConstants.Admin]}
			/>

			<PrivateRoute
				path={`${routes.ADMIN}/funcionarios`}
				layout={EmployeeLayout}
				module={EmployeeRoutes.AdminUserSearchModule}
				roles={[RoleConstants.Admin]}
			/>

			<PrivateRoute
				exact
				path={`${routes.ADMIN}/funcionario`}
				layout={EmployeeLayout}
				module={EmployeeRoutes.AdminUserManagementModule}
				roles={[RoleConstants.Admin]}
			/>

			<PrivateRoute
				path={`${routes.ADMIN}/funcionario/:id`}
				layout={EmployeeLayout}
				module={EmployeeRoutes.AdminUserManagementModule}
				roles={[RoleConstants.Admin]}
			/>

			<PrivateRoute
				path={`${routes.ADMIN}/criar-pontos`}
				layout={EmployeeLayout}
				module={EmployeeRoutes.AdminCreatePointsModule}
				roles={[RoleConstants.Admin]}
			/>

			<PrivateRoute
				path={`${routes.ADMIN}/movimentar-pontos`}
				layout={EmployeeLayout}
				module={EmployeeRoutes.AdminMovePointsModule}
				roles={[RoleConstants.Admin]}
			/>

			<PrivateRoute
				path={`${routes.ADMIN}/relatorios/clientes`}
				layout={EmployeeLayout}
				module={EmployeeRoutes.AdminClientsReportModule}
				roles={[RoleConstants.Admin]}
			/>

			<PrivateRoute
				path={`${routes.ADMIN}/relatorios/extrato-de-pontos`}
				layout={EmployeeLayout}
				module={EmployeeRoutes.AdminPointsExtractReportModule}
				roles={[RoleConstants.Admin]}
			/>

			<PrivateRoute
				path={`${routes.ADMIN}/relatorios/vendas-faturamento`}
				layout={EmployeeLayout}
				module={EmployeeRoutes.AdminSalesRevenueReportModule}
				roles={[RoleConstants.Admin]}
			/>

			<PrivateRoute
				path={`${routes.ADMIN}/relatorios/clube-moveflex`}
				layout={EmployeeLayout}
				module={EmployeeRoutes.AdminClubReportModule}
				roles={[RoleConstants.Admin]}
			/>

			<PrivateRoute
				path={`${routes.ADMIN}/relatorios/breakage`}
				layout={EmployeeLayout}
				module={EmployeeRoutes.AdminPointsBreakageReportModule}
				roles={[RoleConstants.Admin]}
			/>

			<PrivateRoute
				path={`${routes.ADMIN}/relatorios/solicitacoes-cartao`}
				layout={EmployeeLayout}
				module={EmployeeRoutes.AdminCardProposalsReportModule}
				roles={[RoleConstants.Admin]}
			/>

			<PrivateRoute
				path={`${routes.ADMIN}/relatorios/faturas`}
				layout={EmployeeLayout}
				module={EmployeeRoutes.AdminInvoicesReportModule}
				roles={[RoleConstants.Admin]}
			/>

			<PrivateRoute
				path={`${routes.ADMIN}/cupons-de-desconto`}
				layout={EmployeeLayout}
				module={EmployeeRoutes.AdminCouponSearchModule}
				roles={[RoleConstants.Admin]}
			/>

			<PrivateRoute
				exact
				path={`${routes.ADMIN}/loja-moveflex`}
				layout={EmployeeLayout}
				module={EmployeeRoutes.AdminStoreProductSearchModule}
				roles={[RoleConstants.Admin]}
			/>

			<PrivateRoute
				path={`${routes.ADMIN}/loja-moveflex/novo-produto`}
				layout={EmployeeLayout}
				module={EmployeeRoutes.AdminStoreProductCreateModule}
				roles={[RoleConstants.Admin]}
			/>

			<PrivateRoute
				path={`${routes.ADMIN}/loja-moveflex/:productid`}
				layout={EmployeeLayout}
				module={EmployeeRoutes.AdminStoreProductEditModule}
				roles={[RoleConstants.Admin]}
			/>

			<PrivateRoute
				path={`${routes.ADMIN}/incluir-cupom`}
				layout={EmployeeLayout}
				module={EmployeeRoutes.AdminCouponCreationModule}
				roles={[RoleConstants.Admin]}
			/>

			<PrivateRoute
				path={`${routes.ADMIN}/cupom-de-desconto/:couponId`}
				layout={EmployeeLayout}
				module={EmployeeRoutes.AdminCouponDetailsModule}
				roles={[RoleConstants.Admin]}
			/>

			<PrivateRoute
				path={`${routes.ADMIN}/passagens-vendidas`}
				layout={EmployeeLayout}
				module={PartnerRoutes.PartnerTicketsSalesReportModule}
				roles={[RoleConstants.Admin]}
			/>

			<PrivateRoute
				path={`${routes.CLIENTE}/meus-dados`}
				layout={ClientLayout}
				module={ClientRoutes.ClientInformationModule}
				roles={[RoleConstants.Client]}
			/>

			<PrivateRoute
				path={`${routes.CLIENTE}/extrato`}
				layout={ClientLayout}
				module={ClientRoutes.ClientExtractOfPointsModule}
				roles={[RoleConstants.Client]}
			/>

			<PrivateRoute
				path={`${routes.CLIENTE}/compra-de-pontos`}
				layout={ClientLayout}
				module={ClientRoutes.PointsPurchaseModule}
				roles={[RoleConstants.Client]}
			/>

			<PrivateRoute
				path={`${routes.CLIENTE}/compra-de-pontos-presente`}
				layout={ClientLayout}
				module={ClientRoutes.PointsPurchaseGiftModule}
				roles={[RoleConstants.Client]}
			/>

			<PrivateRoute
				path={`${routes.CLIENTE}/compra-efetuada`}
				layout={ClientLayout}
				module={ClientRoutes.PurchaseSuccessModule}
				roles={[RoleConstants.Client]}
			/>

			<PrivateRoute
				path={`${routes.CLIENTE}/erro-processamento`}
				layout={ClientLayout}
				module={ClientRoutes.ProcessErrorModule}
				roles={[RoleConstants.Client]}
			/>

			<PrivateRoute
				path={`${routes.CLIENTE}/minhas-compras`}
				layout={ClientLayout}
				module={ClientRoutes.MyPurchasesModule}
				roles={[RoleConstants.Client]}
			/>

			<PrivateRoute
				path={`${routes.CLIENTE}/clube-sucesso`}
				layout={ClientLayout}
				module={ClientRoutes.ClubSuccessModule}
				roles={[RoleConstants.Client]}
			/>

			<PrivateRoute
				path={`${routes.CLIENTE}/aguardando-transferencia`}
				layout={ClientLayout}
				module={ClientRoutes.WaitingTransferModule}
				roles={[RoleConstants.Client]}
			/>

			<PrivateRoute
				path={`${routes.CLIENTE}/planos`}
				layout={ClientLayout}
				module={ClientRoutes.PlansModule}
				roles={[RoleConstants.Client]}
			/>

			<PrivateRoute
				path={`${routes.CLIENTE}/meu-plano`}
				layout={ClientLayout}
				module={ClientRoutes.MyPlanModule}
				roles={[RoleConstants.Client]}
			/>

			<PrivateRoute
				path={`${routes.CLIENTE}/historico-de-pagamentos`}
				layout={ClientLayout}
				module={ClientRoutes.PayHistoryModule}
				roles={[RoleConstants.Client]}
			/>

			<PrivateRoute
				path={`${routes.CLIENTE}/regularizar-assinatura`}
				layout={ClientLayout}
				module={ClientRoutes.RegularizePaymentModule}
				roles={[RoleConstants.Client]}
			/>

			<PrivateRoute
				path={`${routes.CLIENTE}/cancelar-assinatura`}
				layout={ClientLayout}
				module={ClientRoutes.CancelSubscriptionModule}
				roles={[RoleConstants.Client]}
			/>

			<PrivateRoute
				path={`${routes.CLIENTE}/convide-seus-amigos`}
				layout={ClientLayout}
				module={ClientRoutes.InviteYourFriendsModule}
				roles={[RoleConstants.Client]}
			/>

			<PrivateRoute
				exact
				path={`${routes.CLIENTE}/loja-muviflex`}
				layout={ClientLayout}
				module={SiteRoutes.PointsExchangeModule}
				roles={[RoleConstants.Client]}
			/>

			<PrivateRoute
				path={`${routes.CLIENTE}/loja-muviflex/pedido-confirmado`}
				layout={ClientLayout}
				module={ClientRoutes.PointsTransferModule}
				roles={[RoleConstants.Client]}
			/>

			<PrivateRoute
				exact
				path={`${routes.CLIENTE}/compre-e-ganhe`}
				layout={ClientLayout}
				module={ClientRoutes.BuyWinModule}
				roles={[RoleConstants.Client]}
			/>

			<PrivateRoute
				path={`${routes.CLIENTE}/compre-e-ganhe/:partner`}
				layout={ClientLayout}
				module={ClientRoutes.BuyWinPartnerModule}
				roles={[RoleConstants.Client]}
			/>

			<PrivateRoute
				path={`${routes.CLIENTE}/loja/resgatar/:id`}
				layout={ClientLayout}
				module={ClientRoutes.StoreProductRedeemModule}
				roles={[RoleConstants.Client]}
			/>

			<PrivateRoute
				path={`${routes.CLIENTE}/loja/produto/:id`}
				layout={ClientLayout}
				module={SiteRoutes.StoreProductDetailModule}
				roles={[RoleConstants.Client]}
			/>

			<PrivateRoute
				path={`${routes.CLIENTE}/loja/produto-resgatado`}
				layout={ClientLayout}
				module={ClientRoutes.StorePurchaseSuccessModule}
				roles={[RoleConstants.Client]}
			/>

			<PrivateRoute
				path={`${routes.CLIENTE}/solicitacao-de-pontos`}
				layout={ClientLayout}
				module={ClientRoutes.PointsSolicitationModule}
				roles={[RoleConstants.Client]}
			/>

			<PrivateRoute
				exact
				path={`${routes.CLIENTE}${routes.COMPRA_DE_PASSAGEM}`}
				layout={ClientLayout}
				module={SiteRoutes.TicketSaleModule}
				roles={[RoleConstants.Client]}
			/>

			<PrivateRoute
				exact
				path={routes.PARCEIRO}
				layout={PartnerLayout}
				module={PartnerRoutes.PartnerModule}
				roles={[RoleConstants.Partner]}
			/>

			<PrivateRoute
				path={`${routes.PARCEIRO}/passagens-vendidas`}
				layout={PartnerLayout}
				module={PartnerRoutes.PartnerTicketsSalesReportModule}
				roles={[RoleConstants.Partner]}
			/>

			<PrivateRoute
				path={`${routes.PARCEIRO}/extrato-de-pontos`}
				layout={PartnerLayout}
				module={PartnerRoutes.PartnerPointsExtractModule}
				roles={[RoleConstants.Partner]}
			/>
			<Redirect from="*" to="/" />
		</Switch>
	)
}

store.dispatch(BonusPointsCreators.loadRoundTripPoints())

const MainRouter = () => (
	<ErrorBoundary>
		<Provider store={store}>
			<Router history={history}>
				<ScrollToTop>
					<RootRoutes />
					<Toast />
					<MainLoader />
				</ScrollToTop>
			</Router>
		</Provider>
	</ErrorBoundary>
)

export { MainRouter, Register }
